import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { signal, OnInit, effect } from '@angular/core';
import { environment } from '../../environments/environment';
import { ApiAvailability } from '../models/api-availability.model';
import { DatevAuthStatusService } from '../services/datev-auth-status.service';
import { NotificationService } from '../services/notification.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [RouterLink, CommonModule],
  template: `
    <div class="home-container">
      <div class="api-availability-section">
        <div class="section-header">
          <h2>Verfügbarkeit der Schnittstellen</h2>
          <button class="refresh-button"
                  *ngIf="datevAuthService.getAuthStatus()()"
                  (click)="loadApiAvailability()"
                  [disabled]="loading()"
                  [class.refreshing]="loading()">
            <svg class="refresh-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.5 2v6h-6"/>
              <path d="M2.5 22v-6h6"/>
              <path d="M2.5 11.5a10 10 0 0 1 18.4-4.5"/>
              <path d="M21.5 12.5a10 10 0 0 1-18.4 4.5"/>
            </svg>
            Aktualisieren
          </button>
        </div>

        <div class="auth-required-message" *ngIf="!datevAuthService.getAuthStatus()()">
          <svg class="warning-icon" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#856404" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <h4>DATEV Authentifizierung erforderlich</h4>
          <p>Um die Verfügbarkeit der Schnittstellen zu prüfen, müssen Sie sich zuerst bei DATEV anmelden.</p>
          <button class="datev-login-button" (click)="loginDatev()">
            Bei DATEV anmelden
          </button>
        </div>

        <div *ngIf="datevAuthService.getAuthStatus()()">
          <div class="loading-spinner" *ngIf="loading()">
            <svg class="spinner-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#007bff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
            Lade Schnittstellen-Status...
          </div>

          <div class="error-message" *ngIf="error()">
            {{ error() }}
          </div>

          <div class="api-list" *ngIf="!loading() && !error() && apiAvailabilities().length > 0">
            <div class="api-item" *ngFor="let api of apiAvailabilities()"
                 [class.available]="api.accessable"
                 [class.unavailable]="!api.accessable">
              <div class="api-header" [class.no-message]="!api.message">
                <span class="api-name">{{ getApiDisplayName(api['apiName']) }}</span>
                <div class="api-status-container">
                  <span class="api-status"
                        [class.status-available]="api.accessable"
                        [class.status-unavailable]="!api.accessable">
                  {{ api.accessable ? 'Verfügbar' : 'Nicht verfügbar' }}
                  </span>
                  <button class="sync-icon-button"
                          *ngIf="api.apiName.toLowerCase() === 'datenaustausch(hr:exchange)' && api.accessable"
                          (click)="synchronizeEmployees()"
                          [disabled]="synchronizing()"
                          [class.syncing]="synchronizing()"
                          title="Mitarbeiter synchronisieren">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21.5 2v6h-6"/>
                      <path d="M2.5 22v-6h6"/>
                      <path d="M2.5 11.5a10 10 0 0 1 18.4-4.5"/>
                      <path d="M21.5 12.5a10 10 0 0 1-18.4 4.5"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div class="api-message" *ngIf="api.message">
                {{ api.message }}
              </div>
            </div>
          </div>

          <div class="no-data" *ngIf="!loading() && !error() && apiAvailabilities().length === 0">
            Keine Schnittstellen-Informationen verfügbar.
          </div>

        </div>
      </div>
    </div>
  `,
  styles: [`
    .home-container {
      max-width: 800px;
      margin: 40px auto;
      padding: 20px;
    }

    .api-availability-section {
      margin-top: 40px;
      padding: 20px;
      background-color: #f8f9fa;
      border-radius: 8px;
      border: 1px solid #e9ecef;
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      border-bottom: 2px solid #007bff;
      padding-bottom: 8px;
    }

    .section-header h2 {
      color: #333;
      font-size: 1.5rem;
      margin: 0;
    }

    .loading-spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      text-align: center;
      padding: 20px;
      color: #666;
    }

    .spinner-icon {
      animation: spin 1s linear infinite;
    }

    .error-message {
      background-color: #f8d7da;
      color: #721c24;
      padding: 12px;
      border: 1px solid #f5c6cb;
      border-radius: 4px;
      margin-bottom: 20px;
      text-align: left;
    }

    .api-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .api-item {
      padding: 16px;
      border-radius: 6px;
      border: 1px solid #dee2e6;
      background-color: white;
      transition: box-shadow 0.2s;
      text-align: left;
    }

    .api-item:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .api-item.available {
      border-left: 4px solid #28a745;
    }

    .api-item.unavailable {
      border-left: 4px solid #dc3545;
    }

    .api-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
    }

    .api-header.no-message {
      margin-bottom: 0;
    }

    .api-status-container {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .api-name {
      font-weight: 600;
      font-size: 1.1rem;
      color: #333;
    }

    .api-status {
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 0.875rem;
      font-weight: 500;
    }

    .status-available {
      background-color: #d4edda;
      color: #155724;
      border: 1px solid #c3e6cb;
    }

    .status-unavailable {
      background-color: #f8d7da;
      color: #721c24;
      border: 1px solid #f5c6cb;
    }

    .api-message {
      color: #666;
      font-size: 0.9rem;
      line-height: 1.4;
      margin-top: 8px;
      padding: 8px;
      background-color: #f8f9fa;
      border-radius: 4px;
    }

    .no-data {
      text-align: center;
      color: #666;
      font-style: italic;
      padding: 20px;
    }

    .refresh-button {
      background-color: #007bff;
      color: white;
      padding: 6px 14px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 0.875rem;
      transition: background-color 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .refresh-button:hover:not(:disabled) {
      background-color: #0056b3;
    }

    .refresh-button:disabled {
      background-color: #6c757d;
      cursor: not-allowed;
    }

    .refresh-button.refreshing .refresh-icon {
      animation: spin 1s linear infinite;
    }

    .auth-required-message {
      text-align: center;
      padding: 2rem;
      background-color: #fff3cd;
      border: 1px solid #ffeaa7;
      border-radius: 8px;
      margin-bottom: 2rem;
    }

    .warning-icon {
      margin-bottom: 1rem;
    }

    .auth-required-message h4 {
      color: #856404;
      margin-bottom: 1rem;
    }

    .auth-required-message p {
      color: #856404;
      margin-bottom: 1.5rem;
    }

    .datev-login-button {
      background-color: #007bff;
      color: white;
      padding: 0.75rem 1.5rem;
      border: none;
      border-radius: 4px;
      font-size: 1rem;
      cursor: pointer;
      transition: background-color 0.2s;
      min-height: 44px;
      min-width: 120px;
    }

    .datev-login-button:hover {
      background-color: #0056b3;
    }

    .sync-icon-button {
      background-color: #007bff;
      color: white;
      border: none;
      cursor: pointer;
      padding: 6px;
      border-radius: 4px;
      transition: background-color 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .sync-icon-button:hover:not(:disabled) {
      background-color: #0056b3;
    }

    .sync-icon-button:disabled {
      background-color: #6c757d;
      cursor: not-allowed;
    }

    .sync-icon-button.syncing svg {
      animation: spin 0.8s ease-in-out infinite;
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @media (max-width: 768px) {
      .home-container {
        margin: 20px auto;
        padding: 15px;
      }

      .api-availability-section {
        padding: 15px;
        margin-top: 20px;
      }

      .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
      }

      .auth-required-message {
        padding: 1.5rem;
      }

      .datev-login-button {
        padding: 1rem 2rem;
        font-size: 1.1rem;
        min-height: 48px;
        width: 100%;
        max-width: 280px;
      }

      .api-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
      }

      .api-status-container {
        align-self: flex-start;
      }
    }

    @media (max-width: 480px) {
      .auth-required-message {
        padding: 1rem;
      }

      .warning-icon {
        width: 36px;
        height: 36px;
      }

      .datev-login-button {
        padding: 1.2rem 2rem;
        font-size: 1.2rem;
        min-height: 52px;
        width: 100%;
      }

      .section-header h2 {
        font-size: 1.3rem;
      }
    }
  `]
})
export class HomeComponent implements OnInit {
  apiAvailabilities = signal<ApiAvailability[]>([]);
  loading = signal<boolean>(false);
  error = signal<string | null>(null);
  synchronizing = signal<boolean>(false);
  
  constructor(
    private http: HttpClient,
    public datevAuthService: DatevAuthStatusService,
    private notificationService: NotificationService
  ) {
    // Lade API-Verfügbarkeit automatisch wenn DATEV authentifiziert ist
    effect(() => {
      if (this.datevAuthService.getAuthStatus()()) {
        this.loadApiAvailability();
      } else {
        // Zurücksetzen der Daten wenn nicht authentifiziert
        this.apiAvailabilities.set([]);
        this.error.set(null);
      }
    }, { allowSignalWrites: true });
  }
  
  ngOnInit() {
    // Lade nur wenn bereits authentifiziert
    if (this.datevAuthService.getAuthStatus()()) {
      this.loadApiAvailability();
    }
  }
  
  loadApiAvailability() {
    // Prüfe Authentifizierung vor API-Aufruf
    if (!this.datevAuthService.getAuthStatus()()) {
      this.error.set('DATEV Authentifizierung erforderlich');
      return;
    }
    
    this.loading.set(true);
    this.error.set(null);
    
    this.http.get<ApiAvailability[]>(`${environment.apiUrl}/datev/available-apis`)
      .subscribe({
        next: (apis) => {
          this.apiAvailabilities.set(apis);
          this.loading.set(false);
        },
        error: (error) => {
          this.error.set('Fehler beim Laden der Schnittstellen-Verfügbarkeit: ' + 
                        (error.error?.message || error.message || 'Unbekannter Fehler'));
          this.loading.set(false);
        }
      });
  }
  
  loginDatev() {
    this.datevAuthService.loginDatev();
  }
  
  getApiDisplayName(apiName: string): string {
    switch (apiName.toLowerCase()) {
      case 'eau':
        return 'EAU (Elektronische Arbeitsunfähigkeitsbescheinigung)';
      case 'datenaustausch(hr:exchange)':
        return 'Datenaustausch (HR:Exchange)';
      default:
        return apiName.toUpperCase();
    }
  }


  synchronizeEmployees(): void {
    if (this.synchronizing()) return;
    
    this.synchronizing.set(true);
    
    this.http.post<{message: string; created: number; updated: number; deleted: number}>(`${environment.apiUrl}/datev/synchronize-employees`, {})
      .subscribe({
        next: (response) => {
          const details = `Erstellt: ${response.created}, Aktualisiert: ${response.updated}, Gelöscht: ${response.deleted}`;
          this.notificationService.show(`${response.message} (${details})`, 'success');
          this.synchronizing.set(false);
        },
        error: (error) => {
          const errorMessage = error.error?.message || error.message || 'Fehler bei der Mitarbeiter-Synchronisation';
          this.notificationService.show(errorMessage, 'error');
          this.synchronizing.set(false);
        }
      });
  }
}